/**
 * Journey Dashboard - Main UI Component
 *
 * Displays list of journeys for the authenticated user with:
 * - Journey cards with progress indicators
 * - Create new journey button
 * - Filter/search capabilities
 * - Quick actions (view, continue, delete)
 *
 * @see Documentation/Development/Design Docs/Journey_Feature_Design_v1.0.md Section 6
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Compass, Settings, AlertCircle, Plus } from 'lucide-react';
import { JourneyCreationForm } from '../../components/JourneyCreationForm';
import { FairyDashboardPanel } from '../../components/FairyDashboardPanel';
import { FairyWelcomeModal } from '../../components/FairyWelcomeModal';
import { UserDropdown } from '../../components/auth/UserDropdown';
import { useApiClient } from '../../hooks/useApiClient';
import { JourneyStatusPanel } from '../../components/journey/JourneyStatusPanel';
import type { JourneyStatusSummary } from '../../types/journeyStatus';
import '../../styles/journey.css';
import '../../styles/fairy-dashboard.css';

// ============================================================================
// TYPES (mirrored from backend)
// ============================================================================

type JourneyStatus = 'draft' | 'in_progress' | 'completed' | 'abandoned';

interface JourneyProgress {
  percentage: number;
  stations_completed: number;
  stations_total: number;
  assets_processed: number;
  assets_total: number;
  recommendations_count: number;
}

interface JourneyHealth {
  fair_score?: number;
  data_quality_score?: number;
  ml_readiness_score?: number;
  critical_issues: number;
  high_priority_recommendations: number;
  assets_with_errors: number;
}

interface Journey {
  group_id: string;
  name: string;
  description?: string;
  captain: {
    user_id: string;
    name: string;
    email: string;
  };
  status: JourneyStatus;
  progress: JourneyProgress;
  health: JourneyHealth;
  created_at: string;
  updated_at: string;
}

interface JourneyListResponse {
  journeys: Journey[];
  /** v2.5 SS-02/B2: the canonical status contract per journey, keyed by
   *  group_id — the SAME derivation the workspace renders. May be absent
   *  when derivation failed (honest degradation, never fabricated). */
  status_summaries?: Record<string, JourneyStatusSummary>;
  total: number;
  page: number;
  page_size: number;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface JourneyDashboardProps {
  autoOpenCreate?: boolean;
}

export const JourneyDashboard: React.FC<JourneyDashboardProps> = ({ autoOpenCreate = false }) => {
  const navigate = useNavigate();
  const { authFetch } = useApiClient();
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [statusSummaries, setStatusSummaries] = useState<Record<string, JourneyStatusSummary>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<JourneyStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(autoOpenCreate);
  const [showFairyPanel, setShowFairyPanel] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch journeys on mount
  useEffect(() => {
    fetchJourneys();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only refetch when filter changes
  }, [filter]);

  // Check if first-time user
  useEffect(() => {
    const dismissed = localStorage.getItem('fairy_welcome_dismissed');
    if (!dismissed && journeys.length === 0 && !loading) {
      setIsFirstTime(true);
    }
  }, [journeys, loading]);

  const fetchJourneys = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.append('status', filter);
      }

      const response = await authFetch(`/api/journey?${params}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch journeys: ${response.statusText}`);
      }

      const data: JourneyListResponse = await response.json();
      setJourneys(data.journeys);
      setStatusSummaries(data.status_summaries ?? {});
    } catch (err: unknown) {
      console.error('[JourneyDashboard] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJourney = () => {
    setShowCreateModal(true);
  };

  const handleJourneyCreated = (groupId: string) => {
    setShowCreateModal(false);
    // Navigate to the new journey
    navigate(`/journey/${groupId}`);
  };

  const handleViewJourney = (groupId: string) => {
    navigate(`/journey/${groupId}`);
  };

  const handleDeleteJourney = async (groupId: string) => {
    const journeyName = journeys.find(j => j.group_id === groupId)?.name;
    if (!confirm(`Delete journey${journeyName ? ` "${journeyName}"` : ''}? This removes its analyses and recommendations.`)) {
      return;
    }

    setDeleteError(null);
    try {
      const response = await authFetch(`/api/journey/${groupId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete journey');
      }

      // Refresh list
      fetchJourneys();
    } catch (err: unknown) {
      console.error('[JourneyDashboard] Delete error:', err);
      setDeleteError(
        `${journeyName ? `"${journeyName}"` : 'The journey'} was not deleted (${err instanceof Error ? err.message : 'unknown error'}). It is still in the list — try again.`
      );
    }
  };

  // Filter journeys by search query
  const filteredJourneys = journeys.filter(j =>
    j.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    j.group_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="journey-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>My Data Maturity Journeys</h1>
          <p className="subtitle">
            Organize datasets, interviews, and documents into comprehensive FAIR assessments
          </p>
        </div>
        <div className="header-right">
          <button
            className="btn btn-secondary header-action"
            onClick={() => setShowFairyPanel(true)}
            aria-label="Open FAIRy AI Assistant"
            title="Ask FAIRy about your portfolio"
          >
            <Sparkles size={16} aria-hidden="true" />
            <span>Ask FAIRy</span>
          </button>
          <button
            className="btn btn-secondary header-action"
            onClick={() => navigate('/scope/global/all/evaluate')}
            aria-label="Open Bulk Decision Matrix"
            title="Review FAIR rule decisions across all datasets, schemas, and catalogs"
          >
            <Compass size={16} aria-hidden="true" />
            <span>Decisions</span>
          </button>
          <button
            className="btn btn-icon-only"
            onClick={() => navigate('/settings')}
            aria-label="Settings"
            title="Settings - Manage ontologies, team, and configuration"
          >
            <Settings size={16} aria-hidden="true" />
          </button>
          <button className="btn btn-primary btn-lg header-action" onClick={handleCreateJourney}>
            <Plus size={16} aria-hidden="true" />
            <span>Create New Journey</span>
          </button>
          <UserDropdown />
        </div>
      </div>

      {/* Filters & Search */}
      <div className="dashboard-controls">
        <div className="filter-group">
          <label>Status:</label>
          <select
            className="filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'draft' | 'in_progress' | 'completed' | 'abandoned')}
          >
            <option value="all">All Journeys</option>
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="abandoned">Abandoned</option>
          </select>
        </div>

        <div className="search-group">
          <input
            type="text"
            className="search-input"
            placeholder="Search journeys by name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="results-count">
          {filteredJourneys.length} of {journeys.length} journeys
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading journeys...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-state">
          <AlertCircle size={32} aria-hidden="true" className="error-icon-svg" />
          <h3>Failed to Load Journeys</h3>
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={fetchJourneys}>
            Retry
          </button>
        </div>
      )}

      {/* Delete failure (inline, non-blocking) */}
      {deleteError && (
        <div className="error-banner" role="alert">
          <strong>Delete failed.</strong>
          <p>{deleteError}</p>
          <button className="btn btn-secondary btn-sm" onClick={() => setDeleteError(null)}>Dismiss</button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && journeys.length === 0 && (
        <div className="empty-state">
          <Compass size={48} aria-hidden="true" className="empty-illustration-svg" />
          <h2>Start Your First Data Maturity Journey</h2>
          <p>
            Organize datasets, interviews, and documents into a comprehensive FAIR assessment
            that delivers executive-ready business insights.
          </p>
          <button className="btn btn-primary btn-lg" onClick={handleCreateJourney}>
            Create Your First Journey
          </button>
        </div>
      )}

      {/* Journey Cards Grid */}
      {!loading && !error && filteredJourneys.length > 0 && (
        <div className="journey-grid">
          {filteredJourneys.map((journey) => (
            <JourneyCard
              key={journey.group_id}
              journey={journey}
              statusSummary={statusSummaries[journey.group_id] ?? null}
              onView={handleViewJourney}
              onDelete={handleDeleteJourney}
            />
          ))}
        </div>
      )}

      {/* No Results State (after search) */}
      {!loading && !error && journeys.length > 0 && filteredJourneys.length === 0 && (
        <div className="no-results-state">
          <p>No journeys match your search query: "{searchQuery}"</p>
          <button className="btn btn-secondary" onClick={() => setSearchQuery('')}>
            Clear Search
          </button>
        </div>
      )}

      {/* Journey Creation Modal */}
      <JourneyCreationForm
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleJourneyCreated}
      />

      {/* FAIRy Dashboard Panel */}
      <FairyDashboardPanel
        isOpen={showFairyPanel}
        onClose={() => setShowFairyPanel(false)}
      />

      {/* FAIRy Welcome Modal (First-Time Users) */}
      <FairyWelcomeModal
        isFirstTime={isFirstTime}
        onStartJourney={() => {
          setIsFirstTime(false);
          handleCreateJourney();
        }}
        onDismiss={() => setIsFirstTime(false)}
      />
    </div>
  );
};

// ============================================================================
// JOURNEY CARD COMPONENT
// ============================================================================

interface JourneyCardProps {
  journey: Journey;
  /** Canonical status contract for this journey (null = unavailable). */
  statusSummary: JourneyStatusSummary | null;
  onView: (groupId: string) => void;
  onDelete: (groupId: string) => void;
}

const JourneyCard: React.FC<JourneyCardProps> = ({ journey, statusSummary, onView, onDelete }) => {
  const getHealthColor = (score?: number): string => {
    if (!score) return 'health-unknown';
    if (score >= 4) return 'health-good';
    if (score >= 3) return 'health-fair';
    return 'health-poor';
  };

  const formatDate = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="journey-card">
      {/* Card Header */}
      <div className="card-header">
        <div className="header-left">
          <h3 className="journey-name">{journey.name}</h3>
          <span className="journey-id">{journey.group_id}</span>
        </div>
        {/* v2.5 SS-02/B2 round 1: NO independent lifecycle badge here — the
            status panel below is the SOLE status renderer. A manual fallback
            could show an unqualified green "Completed" for a journey that
            was actually completed WITH EXCEPTIONS; a missing summary must
            render as unavailable instead. */}
      </div>

      {/* Description */}
      {journey.description && (
        <p className="journey-description">{journey.description}</p>
      )}

      {/* v2.5 SS-02/B2: the canonical status contract replaces the frozen
          percentage bar and stored station/asset counters — the SAME
          derivation the workspace renders, so the portfolio can never say
          0-of-5 while the workspace says 4-of-4. Workflow-unknown shows NO
          numerics; a missing summary says so instead of inventing numbers. */}
      <JourneyStatusPanel summary={statusSummary} compact />


      {/* Health Metrics */}
      <div className="health-metrics">
        {journey.health.fair_score != null && (
          <div className={`health-metric ${getHealthColor(journey.health.fair_score)}`}>
            <span className="metric-label">FAIR Score</span>
            <span className="metric-value">{journey.health.fair_score.toFixed(1)}/5</span>
          </div>
        )}

        {journey.health.data_quality_score != null && (
          <div className={`health-metric ${getHealthColor(journey.health.data_quality_score / 20)}`}>
            <span className="metric-label">Data Quality</span>
            <span className="metric-value">{journey.health.data_quality_score}/100</span>
          </div>
        )}

        {journey.health.critical_issues > 0 && (
          <div className="health-metric health-critical">
            <span className="metric-label">Critical Issues</span>
            <span className="metric-value">{journey.health.critical_issues}</span>
          </div>
        )}
      </div>

      {/* Metadata */}
      <div className="card-metadata">
        <span className="metadata-item">
          Created {formatDate(journey.created_at)}
        </span>
        <span className="metadata-item">
          Updated {formatDate(journey.updated_at)}
        </span>
        <span className="metadata-item">
          Owner: {journey.captain.name}
        </span>
      </div>

      {/* Actions */}
      <div className="card-actions">
        <button
          className="btn btn-primary"
          onClick={() => onView(journey.group_id)}
        >
          {journey.status === 'draft' ? 'Continue' : 'View Journey'}
        </button>
        <button
          className="btn btn-secondary"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(journey.group_id);
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
};

export default JourneyDashboard;
